import * as React from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { BASE_URL } from "../../../config/config";
import SafePayModal from "./SafePayModal";

// Paying a bill online: ask the server what it costs right now (bill + the late
// fee it has earned + commission on rent), then hand exactly that to SafePay.
// Guessing the amount client-side is how a payment gets refused at checkout.

// Server bill-type names (what /bill-payment-quote and the checkout expect).
const QUOTE_TYPE = {
  electricity: "kelectric",
  ssgc: "ssgc",
  water: "water",
  maintainance: "maintainance",
  trash: "trash",
  rent: "rent",
  security: "security",
  fixedCharges: "fixedCharges",
};

export function useOnlineBillPayment() {
  const [payment, setPayment] = React.useState(null); // quote + bill, once known
  const [loadingId, setLoadingId] = React.useState(null);

  /**
   * @param {string} backendKey  e.g. "rent", "fixedCharges", "electricity"
   * @param {object} bill        the stored bill record (needs `_id`)
   */
  const startPayment = React.useCallback(async (backendKey, bill) => {
    const billType = QUOTE_TYPE[backendKey];
    if (!billType || !bill?._id) {
      toast.error("Online payment isn't available for this bill yet.");
      return;
    }

    setLoadingId(bill._id);
    try {
      const { data } = await axios.get(
        `${BASE_URL}/bill-payment-quote/${billType}/${bill._id}`
      );
      setPayment({ ...data, bill });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to determine the amount.");
    } finally {
      setLoadingId(null);
    }
  }, []);

  const dialog = payment ? (
    <SafePayModal
      open
      onClose={() => setPayment(null)}
      billType={payment.billType}
      amount={payment.quote.total}
      billData={payment.bill}
      tenantId={payment.tenantId}
      landlordId={payment.landlordId}
      flatId={payment.flatId}
      dueDate={payment.dueDate}
      quote={payment.quote}
    />
  ) : null;

  return { startPayment, loadingId, dialog };
}
